import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AgreementStatusCell } from "@/components/common/AgreementStatusCell";
import type { TenantListDto } from "@/services/admin-tenant.service";
import {
  TENANT_COLUMNS,
  useTenantColumnsStore,
  type TenantColumnId,
} from "@/stores/tenantColumnsStore";
import { formatDateTime, formatCurrency } from "@/lib/utils";
import { TenantActionsDropdown } from "./TenantActionsDropdown";

// ============================================================================
// Types & Constants
// ============================================================================

/**
 * How wide each column is drawn.
 *
 * Kept here rather than in the column store because a width is presentation, not preference — the
 * store holds only what gets written to localStorage. Typing it as a full `Record` over the id
 * union is the point: adding a column to `TENANT_COLUMNS` without giving it a width is a compile
 * error, not a column that silently collapses.
 *
 * Fixed widths, not percentages: Tenant Name carries a full 36-character GUID under the name and
 * Contact stacks a full name over an e-mail, so the excess becomes a horizontal scrollbar instead
 * of squeezing every column into the viewport.
 */
const COLUMN_WIDTHS: Record<TenantColumnId, string> = {
  index: "w-16",
  name: "w-80",
  type: "w-32",
  status: "w-28",
  contact: "w-80",
  phone: "w-36",
  resources: "w-28",
  balance: "w-32",
  bonus: "w-32",
  agreements: "w-44",
  createdAt: "w-40",
  actions: "w-24",
};

interface TenantTableProps {
  tenants: TenantListDto[];
  isLoading?: boolean;
  /** Row number of the first tenant in `tenants`, e.g. (page - 1) * pageSize. */
  startIndex?: number;
}

// ============================================================================
// Component
// ============================================================================

export function TenantTable({
  tenants,
  isLoading = false,
  startIndex = 0,
}: TenantTableProps) {
  // Subscribing to `hidden` and deriving the predicate locally, NOT subscribing to the store's own
  // `isVisible`: that one is a stable function reading the latest state, so a component that
  // selects it re-renders for nothing on every unrelated store write and never re-renders when a
  // column is toggled. `hidden` is the value that actually moves.
  const hiddenColumns = useTenantColumnsStore((state) => state.hidden);
  const isVisible = (id: TenantColumnId) => !hiddenColumns.includes(id);

  // Render loading state
  if (isLoading) {
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const visibleColumnCount = TENANT_COLUMNS.filter((column) =>
    isVisible(column.id),
  ).length;

  return (
    /*
      `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
      table (`<div class="relative w-full overflow-auto">`), and it is what makes the horizontal
      scrollbar reachable. That wrapper is as tall as the whole table, so while IT owned the
      horizontal overflow its scrollbar rendered below the last row — off the bottom of the
      viewport, where nobody could reach it. Switching it off hands both axes to this div, the one
      with a bounded height, so the bar sits at the bottom of the visible table. It also repairs
      the sticky header below: `position: sticky` resolves against the nearest scrollport, and
      while that was the inner wrapper — a div that never scrolls vertically — the header simply
      scrolled away. Same change as the resource and payment lists.
    */
    <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
      <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
        {/* Generated from TENANT_COLUMNS rather than written out, so a hidden column cannot leave
            its <col> behind — which would shift every width after it by one and is the one drift
            the browser gives no error for. A fixed-layout table is widened to the sum of these
            widths, so no `min-w` is needed to make it overflow — only a scrollport that puts the
            bar somewhere visible. */}
        <colgroup>
          {TENANT_COLUMNS.filter((column) => isVisible(column.id)).map(
            (column) => (
              <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
            ),
          )}
        </colgroup>
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            {isVisible("index") && (
              <TableHead className="text-center">STT</TableHead>
            )}
            {isVisible("name") && <TableHead>Tenant Name</TableHead>}
            {isVisible("type") && <TableHead>Type</TableHead>}
            {isVisible("status") && <TableHead>Status</TableHead>}
            {isVisible("contact") && <TableHead>Contact</TableHead>}
            {isVisible("phone") && <TableHead>Phone Number</TableHead>}
            {isVisible("resources") && (
              <TableHead className="text-center">Resources</TableHead>
            )}
            {isVisible("balance") && <TableHead>Balance</TableHead>}
            {/* Its own column, never folded into Balance: bonus credit buys a trial VM and
                nothing else, so a tenant showing 100,000 here has no money to spend. */}
            {isVisible("bonus") && <TableHead>Bonus</TableHead>}
            {/* The list comes back newest-first from the backend and stays that way: the two sort
                headers this column and Tenant Name used to carry are gone. */}
            {isVisible("agreements") && <TableHead>Commitments</TableHead>}
            {isVisible("createdAt") && <TableHead>Created At</TableHead>}
            {isVisible("actions") && (
              <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenants.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumnCount}
                className="h-24 text-center text-muted-foreground"
              >
                No tenants found.
              </TableCell>
            </TableRow>
          ) : (
            tenants.map((tenant, index) => (
              <TableRow key={tenant.id}>
                {isVisible("index") && (
                  <TableCell className="text-center text-muted-foreground">
                    {startIndex + index + 1}
                  </TableCell>
                )}
                {isVisible("name") && (
                  <TableCell className="font-medium">
                    <div className="space-y-0.5">
                      <span
                        className="block truncate font-medium text-foreground"
                        title={tenant.name}
                      >
                        {tenant.name}
                      </span>
                      <p
                        className="truncate font-mono text-[11px] text-muted-foreground"
                        title={tenant.id}
                      >
                        {tenant.id}
                      </p>
                    </div>
                  </TableCell>
                )}
                {isVisible("type") && (
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      {tenant.type}
                    </Badge>
                  </TableCell>
                )}
                {isVisible("status") && (
                  <TableCell>
                    <Badge
                      variant={
                        tenant.status === "Active" ||
                        (tenant.status as unknown) === 1
                          ? "default"
                          : tenant.status === "Pending" ||
                              (tenant.status as unknown) === 0
                            ? "secondary"
                            : "destructive"
                      }
                    >
                      {(tenant.status as unknown) === 0
                        ? "Pending"
                        : (tenant.status as unknown) === 1
                          ? "Active"
                          : (tenant.status as unknown) === 2
                            ? "Suspended"
                            : (tenant.status as unknown) === 3
                              ? "Inactive"
                              : tenant.status}
                    </Badge>
                  </TableCell>
                )}
                {isVisible("contact") && (
                  <TableCell>
                    <div className="min-w-0 text-sm space-y-0.5">
                      <Link
                        to={`/admin/users/${tenant.ownerId}`}
                        className="block truncate font-medium text-pink-600 hover:underline dark:text-pink-400"
                        title={tenant.ownerFullName}
                      >
                        {tenant.ownerFullName}
                      </Link>
                      <div
                        className="truncate text-xs text-muted-foreground"
                        title={tenant.ownerEmail}
                      >
                        {tenant.ownerEmail}
                      </div>
                    </div>
                  </TableCell>
                )}
                {isVisible("phone") && (
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {tenant.ownerPhoneNumber || "—"}
                  </TableCell>
                )}
                {isVisible("resources") && (
                  <TableCell className="text-center">
                    <Badge variant="outline">{tenant.resourceCount}</Badge>
                  </TableCell>
                )}
                {isVisible("balance") && (
                  <TableCell className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(
                      tenant.balance ?? 0,
                      tenant.currency || "VND",
                    )}
                  </TableCell>
                )}
                {isVisible("bonus") && (
                  <TableCell
                    className={
                      tenant.bonusCreditBalance
                        ? "font-semibold text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground"
                    }
                  >
                    {/* 0₫ rather than an em dash: a tenant with no bonus HAS a bonus balance of
                      zero, and the dash read as "not applicable here" — the same mark this table
                      uses in Phone Number for a value that does not exist. The muted colour above
                      still keeps a real bonus the only thing that stands out in the column. */}
                    {formatCurrency(
                      tenant.bonusCreditBalance ?? 0,
                      tenant.bonusCurrency || tenant.currency || "VND",
                    )}
                  </TableCell>
                )}
                {isVisible("agreements") && (
                  <TableCell className="text-sm">
                    <AgreementStatusCell statuses={tenant.agreements} />
                  </TableCell>
                )}
                {isVisible("createdAt") && (
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDateTime(tenant.createdAt)}
                  </TableCell>
                )}
                {isVisible("actions") && (
                  <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                    <div className="flex items-center justify-center">
                      <TenantActionsDropdown tenant={tenant} />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
